import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useSeo } from "../components/Seo";
import { getCandidate } from "../data/site";

const coverageAreas = [
    {
        title: "Candidate interviews & public statements",
        text: "What the candidates say, in their own words, with attribution.",
    },
    {
        title: "Municipal issues",
        text: "The questions facing Milton, Oakville, Burlington and Halton Hills in this election.",
    },
    {
        title: "Campaign announcements",
        text: "Announcements from the mayoral campaigns, reported as supplied.",
    },
    {
        title: "Media coverage",
        text: "Relevant coverage of the 2026 Halton mayoral races, linked with attribution.",
    },
    {
        title: "Civic leadership profiles",
        text: "Editorial profiles of the people shaping Halton's civic life.",
    },
];

const featured = getCandidate("zainab-azim");

export default function Issues() {
    useSeo({
        title: "Issues & Stories — HALTONS.CA",
        description:
            "Candidate interviews, municipal issues, campaign announcements and civic leadership profiles from the 2026 Halton Region mayoral elections.",
        path: "/issues",
    });

    return (
        <main data-testid="issues-page">
            <section className="border-b border-border bg-navy text-ivory">
                <div className="mx-auto max-w-6xl px-4 py-16 sm:px-6 sm:py-20">
                    <p className="text-xs font-semibold uppercase tracking-[0.25em] text-gold">
                        Editorial
                    </p>
                    <h1 className="mt-4 font-serif-display text-4xl font-bold sm:text-5xl">
                        Issues &amp; Stories
                    </h1>
                    <p className="mt-4 max-w-2xl text-sm leading-relaxed text-ivory/75 md:text-base">
                        Editorial coverage of the 2026 mayoral elections in Halton Region. Stories are
                        published as content is supplied to HALTONS.CA — we do not generate stories or
                        publish unsourced material.
                    </p>
                </div>
            </section>

            {featured && (
                <section className="mx-auto max-w-6xl px-4 py-16 sm:px-6" aria-labelledby="featured-story-heading">
                    <p className="text-xs font-semibold uppercase tracking-[0.25em] text-teal">
                        Featured — Emerging Leaders of Halton
                    </p>
                    <div className="mt-6 border border-border bg-white p-8 sm:p-10">
                        <h2 id="featured-story-heading" className="font-serif-display text-2xl font-semibold text-navy sm:text-3xl">
                            {featured.editorialTitle}
                        </h2>
                        <p className="mt-2 font-serif-display text-base italic text-muted-foreground">
                            {featured.name} — Candidate for {featured.office}
                        </p>
                        <p className="mt-5 max-w-3xl text-sm leading-relaxed text-foreground/90 md:text-base">
                            {featured.lede}
                        </p>
                        <Link
                            to={`/candidates/${featured.slug}`}
                            data-testid="issues-featured-link"
                            className="mt-6 inline-flex items-center gap-2 bg-navy px-6 py-3 text-sm font-semibold text-ivory transition-colors hover:bg-teal"
                        >
                            Read the profile
                            <ArrowRight className="h-4 w-4" aria-hidden="true" />
                        </Link>
                        <p className="mt-4 text-xs text-muted-foreground">
                            A selected editorial feature — not an official ranking or endorsement.
                        </p>
                    </div>
                </section>
            )}

            <section className="border-t border-border bg-white" aria-labelledby="coverage-heading">
                <div className="mx-auto max-w-6xl px-4 py-16 sm:px-6">
                    <h2 id="coverage-heading" className="font-serif-display text-2xl font-semibold text-navy sm:text-3xl">
                        What we cover
                    </h2>
                    <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                        {coverageAreas.map((a) => (
                            <div key={a.title} className="border border-border bg-ivory p-6">
                                <div className="h-1 w-10 bg-gold" aria-hidden="true" />
                                <h3 className="mt-4 font-serif-display text-lg font-semibold text-navy">{a.title}</h3>
                                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{a.text}</p>
                            </div>
                        ))}
                    </div>
                    <p className="mt-8 max-w-2xl text-sm text-muted-foreground">
                        Additional interviews, statements and stories will appear here as they are
                        supplied. To suggest a correction to anything published,{" "}
                        <Link to="/corrections" data-testid="issues-corrections-link" className="font-semibold text-teal link-underline">
                            report it here
                        </Link>
                        .
                    </p>
                </div>
            </section>
        </main>
    );
}
